import { useMemo, useState, useEffect, type ReactNode } from "react";
import type { StoreApi } from "zustand";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  createPermissionEvaluator,
  createServiceContainer,
  findActiveNavNode,
  resolveFeatureNavigation,
  resolveFeatureShellSlots,
  ShellHeaderActionsProvider,
  useAuthStore,
  useLanguageStore,
  useUiStore,
  type AppFeatureDefinition,
  type AuthController,
  type AuthState,
  type FeatureNavNode,
  type FeatureRegistry,
  type ServiceContainer,
} from "@nasam/core";
import { cn } from "@nasam/ui";
import { featureRegistry as defaultModuleRegistry } from "../registry";
import { authController as defaultAuthController } from "../auth";
import { NavRail } from "./NavRail";
import { NavDrawer } from "./NavDrawer";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { MobileNav } from "./MobileNav";
import { TopBar } from "./TopBar";

// A stable, never-bound default so a caller that omits `services` (e.g. a
// test rendering AppShell with a feature-free registry fixture) doesn't get
// a fresh container identity on every render.
const EMPTY_SERVICES: ServiceContainer = createServiceContainer();

export interface AppShellProps {
  readonly registry?: FeatureRegistry | undefined;
  readonly authStore?: StoreApi<AuthState> | undefined;
  readonly authController?: AuthController | undefined;
  readonly services?: ServiceContainer | undefined;
  readonly pathname?: string | undefined;
  readonly onNavigate?: ((path: string) => void) | undefined;
  readonly children?: ReactNode | undefined;
}

function hasDescendant(node: FeatureNavNode, targetId: string): boolean {
  if (node.children === undefined || node.children.length === 0) {
    return false;
  }
  for (const child of node.children) {
    if (child.id === targetId) {
      return true;
    }
    if (hasDescendant(child, targetId)) {
      return true;
    }
  }
  return false;
}

function findRootRailNode(
  railNodes: readonly FeatureNavNode[],
  activeNodeId?: string,
): FeatureNavNode | undefined {
  if (activeNodeId === undefined) {
    return undefined;
  }
  for (const node of railNodes) {
    if (node.id === activeNodeId || hasDescendant(node, activeNodeId)) {
      return node;
    }
  }
  return undefined;
}

export function firstLeafPath(nodes: readonly FeatureNavNode[]): string | null {
  for (const node of nodes) {
    if (node.path !== undefined) {
      return node.path;
    }
    const nested = firstLeafPath(node.children ?? []);
    if (nested !== null) {
      return nested;
    }
  }
  return null;
}

interface AppShellViewProps extends AppShellProps {
  readonly currentPathname: string;
  readonly navigateTo: (path: string) => void;
}

function AppShellView({
  registry = defaultModuleRegistry,
  authStore = useAuthStore,
  authController = defaultAuthController,
  services = EMPTY_SERVICES,
  currentPathname,
  navigateTo,
  children,
}: AppShellViewProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const [features, setFeatures] = useState<readonly AppFeatureDefinition[]>(
    () => registry.list(),
  );

  useEffect(() => {
    return registry.subscribe(() => {
      setFeatures(registry.list());
    });
  }, [registry]);

  const [user, setUser] = useState(() => authStore.getState().user);
  const language = useLanguageStore((state) => state.language);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setUser(state.user);
    });
  }, [authStore]);

  const permissions = useMemo(() => {
    return createPermissionEvaluator(user?.permissions ?? []);
  }, [user]);

  const topBarEndSlots = useMemo(() => {
    return resolveFeatureShellSlots(features, permissions, services).map(
      ({ featureId, topBarEnd: Slot }) => <Slot key={featureId} />,
    );
  }, [features, permissions, services]);

  // If a feature does not define a navigation tree, provide a default rail node
  // pointing to feature.basePath so resolveFeatureNavigation evaluates its requiredPermissions.
  const effectiveModules = useMemo(() => {
    return features.map((m) => {
      if (m.navigation !== undefined) {
        return m;
      }
      return {
        ...m,
        navigation: [
          {
            id: m.id,
            titleKey: m.titleKey,
            icon: m.icon,
            placement: "rail" as const,
            path: m.basePath,
          },
        ],
      };
    });
  }, [features]);

  const visibleNavNodes = useMemo(() => {
    return resolveFeatureNavigation(effectiveModules, permissions, user?.userType);
  }, [effectiveModules, permissions, user?.userType]);

  const moduleDefinitions = useMemo(() => {
    // Several CORE features share a rail node id ("core") so their navigation
    // merges into one rail item (resolveFeatureNavigation /
    // mergeFeatureNavNodes); the first feature to claim a nav node id here
    // wins, so a later feature never silently overwrites its
    // navItemComponent/override with its own.
    const map = new Map<string, AppFeatureDefinition>();
    for (const m of features) {
      if (!map.has(m.id)) {
        map.set(m.id, m);
      }
      if (m.navigation !== undefined) {
        for (const node of m.navigation) {
          if (!map.has(node.id)) {
            map.set(node.id, m);
          }
        }
      }
    }
    return map;
  }, [features]);

  const activeNavNode = useMemo(() => {
    return findActiveNavNode(visibleNavNodes, currentPathname);
  }, [visibleNavNodes, currentPathname]);

  const rootRailNode = useMemo(() => {
    return findRootRailNode(visibleNavNodes, activeNavNode?.id);
  }, [visibleNavNodes, activeNavNode?.id]);

  const drawerOpen = useUiStore((state) => state.drawerOpen);
  const activeRailItemId = useUiStore((state) => state.activeRailItem);

  // Keeps the store's "manually selected" rail item aligned with wherever the
  // route actually is, but only in response to a real navigation (this effect
  // depends on the route-derived node's id, not on activeRailItemId itself).
  // That leaves room for handleSelectRailItem below to "peek" a different
  // group's drawer content -- e.g. clicking the shared, path-less "core" icon
  // while on a page outside it -- without that peek being immediately
  // overwritten back to the route's own node.
  useEffect(() => {
    if (rootRailNode !== undefined) {
      useUiStore.getState().setActiveRailItem(rootRailNode.id);
    }
  }, [rootRailNode?.id]);

  const currentRailNode = useMemo(() => {
    if (activeRailItemId !== null) {
      const found = visibleNavNodes.find((n) => n.id === activeRailItemId);
      if (found !== undefined) {
        return found;
      }
    }
    if (rootRailNode !== undefined) {
      return rootRailNode;
    }
    return visibleNavNodes[0];
  }, [rootRailNode, activeRailItemId, visibleNavNodes]);

  const resolvedActiveRailId = currentRailNode?.id ?? activeRailItemId;

  const handleSelectRailItem = (node: FeatureNavNode) => {
    useUiStore.getState().setActiveRailItem(node.id);
    const hasChildren = (node.children?.length ?? 0) > 0;
    if (hasChildren && !drawerOpen) {
      useUiStore.getState().setDrawerOpen(true);
    }

    // Only a node that is itself a real route navigates. A pure group/parent
    // icon -- no own path, e.g. the shared "core" rail item -- just reveals
    // its drawer content in place; it never jumps the user to whichever leaf
    // happens to be first.
    if (node.path !== undefined) {
      navigateTo(node.path);
    }
  };

  // The mobile panel has no second-step drawer to reveal -- selecting a
  // group there has to land somewhere, so (unlike the desktop rail above) it
  // still falls back to the group's first leaf.
  const handleSelectMobileRailItem = (node: FeatureNavNode) => {
    useUiStore.getState().setActiveRailItem(node.id);
    if (node.path !== undefined) {
      navigateTo(node.path);
      return;
    }
    const target = firstLeafPath(node.children ?? []);
    if (target !== null) {
      navigateTo(target);
    }
  };

  const handleToggleDrawer = () => {
    useUiStore.getState().toggleDrawer();
  };

  // Mirrors NavDrawer's own render gate: if the active rail node has no
  // children, NavDrawer renders nothing regardless of drawerOpen, so the grid
  // column must stay collapsed too or main reserves space for an empty drawer.
  const hasDrawerItems = (currentRailNode?.children?.length ?? 0) > 0;
  const drawerColumnOpen = drawerOpen && hasDrawerItems;

  // A route can opt into owning its own scrolling (see FeatureNavNode.fillHeight)
  // instead of the default scrolling content column — a map workspace, say.
  const fillHeight = activeNavNode?.fillHeight === true;
  // A screen that draws its own header (FeatureNavNode.hideTopBar) gets the
  // top bar's controls to place there instead (ShellHeaderActions).
  const hideTopBar = activeNavNode?.hideTopBar === true;
  const headerActions = useMemo(
    () => (
      <>
        {topBarEndSlots}
        <LanguageSwitcher
          className="flex size-9 items-center justify-center rounded-md"
          tooltipSide="bottom"
          testId="screen-header-language-switcher"
        />
      </>
    ),
    [topBarEndSlots],
  );

  return (
    <div
      data-testid="app-shell"
      className="flex h-dvh w-full gap-1 overflow-hidden bg-neutral-50 p-2 text-text md:h-screen md:gap-2 md:px-[16px] md:py-[24px]"
    >
      <MobileNav
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        railNodes={visibleNavNodes}
        activeRailItemId={resolvedActiveRailId}
        onSelectRailItem={handleSelectMobileRailItem}
        authController={authController}
        authStore={authStore}
      />
      <NavRail
        railNodes={visibleNavNodes}
        moduleDefinitions={moduleDefinitions}
        activeRailItemId={resolvedActiveRailId}
        onSelectRailItem={handleSelectRailItem}
        authController={authController}
        authStore={authStore}
      />
      {/* Figma 2711:110089 — drawer and content share one white card. Grid,
          not flex: grid-template-columns is animatable (fr included), so the
          drawer column collapses to 0 in step with NavDrawer's own fade/slide
          instead of main snapping over in one frame. The two durations below
          must track NavDrawer's animate-in/out (200ms open, CLOSE_TRANSITION_MS
          140ms close) or the column and the drawer content drift apart. */}
      <div
        className={cn(
          "relative grid grid-cols-1 min-w-0 flex-1 rounded-xl border border-container-default-border bg-container-default-background transition-[grid-template-columns]",
          // ease-emphasized/ease-exit aren't in tailwind-merge's known class
          // groups, so it won't dedupe them — keep them mutually exclusive
          // here rather than layering one over a shared base.
          // Collapsed track is 0rem, not 0fr: a length only interpolates
          // against another length, and fr would make the transition discrete.
          drawerColumnOpen
            ? "md:grid-cols-[15rem_1fr] duration-[200ms] ease-emphasized"
            : "md:grid-cols-[0rem_1fr] duration-[140ms] ease-exit",
        )}
      >
        {/* The sole drawer control: lifted out of NavDrawer so it stays
            mounted (and clickable) even when the drawer is collapsed.
            Positioned against this relative grid container, straddling the
            rail's inner (content-facing) border: the size-6 (24px) button's
            center sits on that edge — 0.5rem (md:gap-2) gap + 12px half-button
            back from the card. `-start-` is logical, so this mirrors correctly
            in RTL. z-50 paints it above the rail. */}
        {hasDrawerItems && (
          <button
            type="button"
            data-testid="drawer-toggle-button"
            aria-label={drawerOpen ? "Collapse drawer" : "Expand drawer"}
            onClick={handleToggleDrawer}
            className="absolute top-[4.75rem] -start-[1.25rem] z-50 hidden size-6 items-center justify-center rounded-full bg-purple-500 text-neutral-white shadow-sm transition-colors duration-150 ease-emphasized hover:bg-purple-500/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-white md:flex"
          >
            {drawerOpen ? (
              <ChevronLeft className="size-4 shrink-0 rtl:rotate-180" />
            ) : (
              <ChevronRight className="size-4 shrink-0 rtl:rotate-180" />
            )}
            <span className="sr-only">
              {drawerOpen ? "Collapse drawer" : "Expand drawer"}
            </span>
          </button>
        )}
        <NavDrawer
          isOpen={drawerOpen}
          activeRailNode={currentRailNode}
          activeNavNode={activeNavNode}
          onNavigate={navigateTo}
          user={user}
          language={language}
        />
        {/* Pinned to track 2: NavDrawer renders nothing when the rail node
            has no children, and auto-placement would otherwise drop main into
            the collapsed first track and give it zero width. */}
        <div className="col-start-2 max-md:col-start-1 flex min-w-0 flex-col overflow-hidden">
          {/* Mobile keeps the top bar even when hidden on desktop: it holds
              the nav menu button. */}
          <div className={hideTopBar ? "md:hidden" : "contents"}>
            <TopBar
              onOpenMobileNav={() => setMobileNavOpen(true)}
              // The page's own rail, not one the drawer is only peeking at.
              currentRailNode={rootRailNode ?? currentRailNode}
              activeNavNode={activeNavNode}
              user={user}
              onNavigate={navigateTo}
              topBarEndSlots={topBarEndSlots}
            />
          </div>
          <main
            id="main-content"
            aria-label="Content"
            className={cn(
              "min-w-0 flex-1",
              // A size container, so a page can bound itself to the visible
              // height (100cqh) and scroll only part of itself.
              fillHeight
                ? "flex flex-col overflow-hidden"
                : "scrollbar-inset overflow-y-auto @container-size",
            )}
          >
            {/* Keyed on the route so each swap remounts and replays the enter.
                Scrolling routes get a full-height column so a page can pin
                content (e.g. form actions) to the bottom with `flex-1`. */}
            <div
              key={currentPathname}
              className={cn(
                "animate-rise",
                fillHeight
                  ? "flex min-h-0 flex-1 flex-col"
                  : "flex min-h-full flex-col",
              )}
            >
              <ShellHeaderActionsProvider value={headerActions}>
                {children}
              </ShellHeaderActionsProvider>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function RoutedAppShell(props: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <AppShellView
      {...props}
      currentPathname={props.pathname ?? location.pathname}
      navigateTo={props.onNavigate ?? ((to) => void navigate({ to }))}
    >
      {props.children ?? <Outlet />}
    </AppShellView>
  );
}

export function AppShell(props: AppShellProps) {
  // If props provide pathname and onNavigate, render AppShellView directly without
  // requiring an active TanStack Router context (useful for isolated unit testing).
  if (props.pathname !== undefined && props.onNavigate !== undefined) {
    return (
      <AppShellView
        {...props}
        currentPathname={props.pathname}
        navigateTo={props.onNavigate}
      />
    );
  }

  return <RoutedAppShell {...props} />;
}
